import { DateTime } from "luxon";

import { styled } from "@mui/material";

import LINKS from "@app/common/links";
import ROUTES from "@app/common/routes";
import { formatTime } from "@app/common/utils/datetime";

import AlertBanner from "@rui/AlertBanner";
import Link from "@rui/Link";
import { Row } from "@rui/Stack";

const AlertBannerActionLink = styled(Link)(({ theme }) => ({
  fontFamily: theme.typography.fontFamilySecondary,
  fontWeight: 600,
}));

interface SessionErrorAlertProps {
  end?: DateTime;
}

export const SessionErrorAlert = ({ end }: SessionErrorAlertProps) => {
  const alertText = `Unfortunately, the Read bot crashed ${end ? `at ${formatTime(end)}. Only` : "and only"} partial data is available for this meeting report.`;
  return (
    <AlertBanner title="Bot Error" severity="error">
      {alertText}
    </AlertBanner>
  );
};

export const SessionCancelledAlert = () => {
  return (
    <AlertBanner
      action={
        <Row spacing={2} pl={1}>
          <AlertBannerActionLink to={ROUTES.CALENDAR}>
            Update Calendar
          </AlertBannerActionLink>
          <AlertBannerActionLink underline href={LINKS.SUPPORT.READ_DIDNT_JOIN}>
            Troubleshoot
          </AlertBannerActionLink>
        </Row>
      }
      severity="info"
      title="Session cancelled"
    >
      Either no participants attended this session, or the bot was unable to
      join. Consider removing Read from future instances of this meeting or
      visit Read support to troubleshoot your settings.
    </AlertBanner>
  );
};

export const SessionRestrictedDataAlert = () => {
  return (
    <AlertBanner
      action={
        <AlertBannerActionLink to={ROUTES.USER_SETTINGS_INTEGRATIONS} pl={1}>
          Enable Zoom Access
        </AlertBannerActionLink>
      }
      severity="error"
      title="Restricted Data"
    >
      Zoom meeting access is disabled. Read cannot automatically record, so this
      report has no data. To fix, enable Zoom meeting access in your settings.
    </AlertBanner>
  );
};

export const SessionUnsupportedLanguageAlert = () => (
  <AlertBanner severity="info" title="Language not supported">
    Read currently{" "}
    <Link underline href={LINKS.SUPPORT.SUPPORTED_LANGUAGES}>
      does not support
    </Link>{" "}
    the primary language detected in this meeting, so no transcript or meeting
    notes were generated.
  </AlertBanner>
);
